import { useState } from 'react';
import { Upload, Mail, User, FileText, X, CheckCircle, Camera, Briefcase, Globe, Video, Image } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROFESSIONAL_ROLES = ["Photographer", "Videographer", "Photo Editor", "Video Editor"];
const MAX_IMAGES = 4;
const MAX_VIDEO_SIZE = 50 * 1024 * 1024; // 50MB

const ProfessionalSignUp = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'Photographer',
    experience: '',
    description: '',
    portfolio: '',
    availability: 'Available for bookings'
  });
  const [specialties, setSpecialties] = useState([]);
  const [specialtyInput, setSpecialtyInput] = useState('');
  const [images, setImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [video, setVideo] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const addSpecialty = () => {
    if (specialtyInput.trim() && !specialties.includes(specialtyInput.trim())) {
      setSpecialties(prev => [...prev, specialtyInput.trim()]);
      setSpecialtyInput('');
    }
  };

  const removeSpecialty = (specialty) => {
    setSpecialties(prev => prev.filter(s => s !== specialty));
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    const remainingSlots = MAX_IMAGES - images.length;
    
    if (files.length > remainingSlots) {
      toast({
        title: "Too many images",
        description: `You can only upload ${MAX_IMAGES} images. ${remainingSlots} slots remaining.`,
        variant: "destructive",
      });
      return;
    }

    const validFiles = files.filter(file => {
      if (!file.type.startsWith('image/')) {
        toast({
          title: "Invalid file type",
          description: `${file.name} is not an image file.`,
          variant: "destructive",
        });
        return false;
      }
      return true;
    });

    setImages(prev => [...prev, ...validFiles]);

    // Create previews
    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviews(prev => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleVideoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm'];
    if (!allowedTypes.includes(file.type)) {
      toast({
        title: "Invalid video format",
        description: "Please upload MP4, MOV, AVI, or WebM format.",
        variant: "destructive",
      });
      return;
    }

    if (file.size > MAX_VIDEO_SIZE) {
      toast({
        title: "Video too large",
        description: "Maximum video size is 50MB.",
        variant: "destructive",
      });
      return;
    }

    setVideo(file);
    setVideoPreview(URL.createObjectURL(file));
  };

  const removeVideo = () => {
    setVideo(null);
    if (videoPreview) {
      URL.revokeObjectURL(videoPreview);
    }
    setVideoPreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email) {
      toast({
        title: "Missing information",
        description: "Please fill in your name and email.",
        variant: "destructive",
      });
      return;
    }

    if (images.length === 0) {
      toast({
        title: "Images required",
        description: "Please upload at least one portfolio image.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    setUploadProgress(10);

    try {
      // Create FormData for multipart upload
      const submitData = new FormData();
      submitData.append('name', formData.name);
      submitData.append('email', formData.email);
      submitData.append('role', formData.role);
      submitData.append('experience', formData.experience);
      submitData.append('description', formData.description);
      submitData.append('portfolio', formData.portfolio);
      submitData.append('availability', formData.availability);
      submitData.append('specialties', JSON.stringify(specialties));

      // Add images
      images.forEach((image, index) => {
        submitData.append('images', image);
      });

      // Add video if present
      if (video) {
        submitData.append('video', video);
      }

      setUploadProgress(30);

      const response = await axios.post(`${API}/professionals/signup-with-files`, submitData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 60) / progressEvent.total) + 30;
          setUploadProgress(Math.min(progress, 90));
        }
      });

      setUploadProgress(100);
      setSubmitted(true);
      toast({
        title: "Application Submitted!",
        description: "We'll review your application and get back to you soon.",
      });

    } catch (error) {
      console.error('Error submitting application:', error);
      toast({
        title: "Submission Failed",
        description: error.response?.data?.detail || "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
      setUploadProgress(0);
    }
  };

  if (submitted) {
    return (
      <div className="bg-black text-white min-h-screen pt-32 pb-20">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-lg mx-auto">
            <div className="w-20 h-20 rounded-full bg-green-600/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="text-green-500" size={40} />
            </div>
            <h1 className="font-display text-4xl font-bold text-white mb-4">
              Application Submitted!
            </h1>
            <p className="text-gray-400 mb-8">
              Thank you for applying to join EWS Productions' professional network. 
              We'll review your application and contact you soon.
            </p>
            <a 
              href="/professionals"
              className="inline-block bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-full font-mono text-sm tracking-widest uppercase transition-all"
            >
              View Professional Directory
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-black text-white min-h-screen pt-32 pb-20">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Header */}
        <div className="max-w-4xl mx-auto text-center mb-12">
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 rounded-full bg-red-600/10 flex items-center justify-center">
              <Briefcase className="text-red-500" size={36} />
            </div>
          </div>
          <h1 className="font-display text-5xl md:text-6xl font-bold text-red-500 mb-6 uppercase">
            Join Our Team
          </h1>
          <p className="text-xl text-gray-300 leading-relaxed max-w-2xl mx-auto">
            Apply to become part of EWS Productions' professional network of photographers, 
            videographers, and editors.
          </p>
          
          <div className="mt-8 bg-zinc-900/50 rounded-lg p-4 border border-red-900/20 inline-block">
            <p className="text-green-400 font-semibold mb-1">✓ Free to Join</p>
            <p className="text-gray-400 text-sm">
              Get listed in our directory and connect with clients – 
              <a 
                href="https://buymeacoffee.com/ewsproductions"
                target="_blank"
                rel="noopener noreferrer"
                className="text-red-400 hover:text-red-300 underline transition-colors"
              >
                buy us a coffee
              </a> if you get booked!
            </p>
          </div>
        </div>

        <div className="max-w-2xl mx-auto">
          <form onSubmit={handleSubmit} className="bg-zinc-900 rounded-lg p-8 border border-red-900/20">
            {/* Name */}
            <div className="mb-6">
              <label className="flex items-center gap-2 text-sm font-mono text-gray-400 mb-2 tracking-wider uppercase">
                <User size={16} /> Full Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full bg-black border border-red-900/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-red-500 transition-colors"
                placeholder="Your full name"
                required
                data-testid="professional-name-input"
              />
            </div>

            {/* Email */}
            <div className="mb-6">
              <label className="flex items-center gap-2 text-sm font-mono text-gray-400 mb-2 tracking-wider uppercase">
                <Mail size={16} /> Email Address *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full bg-black border border-red-900/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-red-500 transition-colors"
                placeholder="your@email.com"
                required
                data-testid="professional-email-input"
              />
            </div>

            {/* Role */}
            <div className="mb-6">
              <label className="flex items-center gap-2 text-sm font-mono text-gray-400 mb-2 tracking-wider uppercase">
                <Camera size={16} /> Professional Role *
              </label>
              <select
                name="role"
                value={formData.role}
                onChange={handleChange}
                className="w-full bg-black border border-red-900/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-red-500 transition-colors"
                data-testid="professional-role-select"
              >
                {PROFESSIONAL_ROLES.map(role => (
                  <option key={role} value={role}>{role}</option>
                ))}
              </select>
            </div>

            {/* Experience */}
            <div className="mb-6">
              <label className="flex items-center gap-2 text-sm font-mono text-gray-400 mb-2 tracking-wider uppercase">
                <Briefcase size={16} /> Experience
              </label>
              <input
                type="text"
                name="experience"
                value={formData.experience}
                onChange={handleChange}
                className="w-full bg-black border border-red-900/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-red-500 transition-colors"
                placeholder="e.g., 5+ years"
              />
            </div>

            {/* Specialties */}
            <div className="mb-6">
              <label className="flex items-center gap-2 text-sm font-mono text-gray-400 mb-2 tracking-wider uppercase">
                Specialties
              </label>
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  value={specialtyInput}
                  onChange={(e) => setSpecialtyInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSpecialty())}
                  className="flex-1 bg-black border border-red-900/30 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-red-500 transition-colors"
                  placeholder="Add specialty (e.g., Fashion, Boudoir, Events)"
                />
                <button
                  type="button"
                  onClick={addSpecialty}
                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors"
                >
                  Add
                </button>
              </div>
              {specialties.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {specialties.map((specialty, idx) => (
                    <span 
                      key={idx}
                      className="bg-red-600/20 text-red-400 px-3 py-1 rounded-full text-sm flex items-center gap-2"
                    >
                      {specialty}
                      <button
                        type="button"
                        onClick={() => removeSpecialty(specialty)}
                        className="hover:text-white transition-colors"
                      >
                        <X size={14} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Description/Bio */}
            <div className="mb-6">
              <label className="flex items-center gap-2 text-sm font-mono text-gray-400 mb-2 tracking-wider uppercase">
                <FileText size={16} /> About You
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={4}
                className="w-full bg-black border border-red-900/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-red-500 transition-colors resize-none"
                placeholder="Tell us about your experience, style, and what makes you unique..."
              />
            </div>

            {/* Portfolio URL */}
            <div className="mb-6">
              <label className="flex items-center gap-2 text-sm font-mono text-gray-400 mb-2 tracking-wider uppercase">
                <Globe size={16} /> Portfolio Website
              </label>
              <input
                type="text"
                name="portfolio"
                value={formData.portfolio}
                onChange={handleChange}
                className="w-full bg-black border border-red-900/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-red-500 transition-colors"
                placeholder="www.yourportfolio.com"
              />
            </div>

            {/* Availability */}
            <div className="mb-6">
              <label className="flex items-center gap-2 text-sm font-mono text-gray-400 mb-2 tracking-wider uppercase">
                Availability
              </label>
              <select
                name="availability"
                value={formData.availability}
                onChange={handleChange}
                className="w-full bg-black border border-red-900/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-red-500 transition-colors"
              >
                <option value="Available for bookings">Available for bookings</option>
                <option value="Limited availability">Limited availability</option>
                <option value="Currently unavailable">Currently unavailable</option>
              </select>
            </div>

            {/* Portfolio Images Upload */}
            <div className="mb-6">
              <label className="flex items-center gap-2 text-sm font-mono text-gray-400 mb-2 tracking-wider uppercase">
                <Image size={16} /> Portfolio Images * (Up to {MAX_IMAGES})
              </label>
              <p className="text-gray-500 text-sm mb-3">
                Upload your best work samples. At least one image is required.
              </p>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                {imagePreviews.map((preview, index) => (
                  <div key={index} className="relative aspect-square">
                    <img 
                      src={preview} 
                      alt={`Preview ${index + 1}`} 
                      className="w-full h-full object-cover rounded-lg border-2 border-red-600/30"
                    />
                    <button
                      type="button"
                      onClick={() => removeImage(index)}
                      className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full p-1 hover:bg-red-700 transition-colors"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
                
                {images.length < MAX_IMAGES && (
                  <label className="aspect-square flex flex-col items-center justify-center border-2 border-dashed border-red-900/30 rounded-lg cursor-pointer hover:border-red-500 transition-colors">
                    <Upload className="text-red-500 mb-2" size={24} />
                    <span className="text-gray-500 text-xs text-center px-2">Add Image</span>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageChange}
                      className="hidden"
                    />
                  </label>
                )}
              </div>
              <p className="text-gray-600 text-xs">{images.length}/{MAX_IMAGES} images uploaded</p>
            </div>

            {/* Portfolio Video Upload */}
            <div className="mb-8">
              <label className="flex items-center gap-2 text-sm font-mono text-gray-400 mb-2 tracking-wider uppercase">
                <Video size={16} /> Portfolio Video (Optional)
              </label>
              <p className="text-gray-500 text-sm mb-3">
                Upload a showreel or portfolio video (max 50MB, MP4/MOV/AVI/WebM)
              </p>
              
              {videoPreview ? (
                <div className="relative">
                  <video 
                    src={videoPreview} 
                    controls 
                    className="w-full rounded-lg border-2 border-red-600/30"
                    style={{ maxHeight: '300px' }}
                  />
                  <button
                    type="button"
                    onClick={removeVideo}
                    className="absolute top-2 right-2 bg-red-600 text-white rounded-full p-2 hover:bg-red-700 transition-colors"
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center h-32 border-2 border-dashed border-red-900/30 rounded-lg cursor-pointer hover:border-red-500 transition-colors">
                  <Video className="text-red-500 mb-2" size={32} />
                  <span className="text-gray-500 text-sm">Click to upload video</span>
                  <span className="text-gray-600 text-xs mt-1">Max 50MB</span>
                  <input
                    type="file"
                    accept="video/mp4,video/quicktime,video/x-msvideo,video/webm"
                    onChange={handleVideoChange}
                    className="hidden"
                  />
                </label>
              )}
            </div>

            {/* Upload Progress */}
            {isSubmitting && uploadProgress > 0 && (
              <div className="mb-6">
                <div className="flex justify-between text-sm text-gray-400 mb-2">
                  <span>Uploading...</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="w-full bg-zinc-800 rounded-full h-2">
                  <div 
                    className="bg-red-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-red-600 hover:bg-red-700 disabled:bg-red-900 disabled:cursor-not-allowed text-white py-4 rounded-full font-mono text-sm tracking-widest uppercase transition-all flex items-center justify-center gap-2"
              data-testid="professional-submit-btn"
            >
              {isSubmitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <CheckCircle size={18} />
                  Submit Application
                </>
              )}
            </button>

            <p className="text-gray-500 text-xs text-center mt-4">
              By submitting, you agree to be listed in our professional directory.
              We'll review your application within 48 hours.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ProfessionalSignUp;
